import {
  LoggerService,
  NetworkService,
  SharedConstantService,
  SharedUserService,
  requireDefined,
} from '@agroone-front/shared'
import {
  Constant,
  ConstantLite,
  Constants,
  ConstantsLite,
  Croptype,
  MsSqlPaginatedData,
  UserDto,
} from '@agroone/entities'
import { HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { catchError, filter, forkJoin, map, mergeMap, Observable, of } from 'rxjs'

@Injectable()
export class ConstantService extends SharedConstantService {
  private logger = inject(LoggerService)
  private userService = inject(SharedUserService)
  protected networkService = inject(NetworkService)

  override baseUrl: string = `${environment.apiUrl}${environment.constants}`

  protected override _constantToConstantLite(constant: Constant): ConstantLite[] {
    const constantLites: ConstantLite[] = []
    const values = requireDefined(constant.values, 'the constant values')
    for (const key of Object.keys(values)) {
      const constantLite: ConstantLite = {
        code: key,
        ...values[key],
        translation: values[key]?.translations?.[0]?.value,
      }
      constantLites.push(constantLite)
    }
    return constantLites
  }

  init(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.userService.user
        .pipe(
          mergeMap((user: UserDto | null) =>
            user ? this.getAll(user.language) : of([{} as Constants, [] as Croptype[]]),
          ),
          filter((tuple): tuple is [Constants, Croptype[]] => Array.isArray(tuple) && tuple.length === 2),
          map(([constants, croptypes]: [Constants, Croptype[]]): [ConstantsLite, ConstantLite[]] => {
            const constantsLite: ConstantsLite = this.constantsToConstantsLite(constants)
            for (const constantName of Object.keys(constantsLite)) {
              const items = constantsLite[constantName]
              if (Array.isArray(items) && items.length > 0 && typeof items[0]?.priority === 'number') {
                constantsLite[constantName] = items.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
              }
            }
            const mappedCroptypes: ConstantLite[] = []
            for (const croptype of croptypes) {
              mappedCroptypes.push({
                croptypeId: croptype.id,
                code: croptype.key,
                regions: croptype.regions,
                translation: croptype.translations?.[0]?.value,
              })
            }
            return [constantsLite, mappedCroptypes]
          }),
          filter((i) => i && Boolean(Object.keys(i).length)),
          catchError((error) => {
            this.logger.error('Constants loading error', JSON.stringify(error), error)
            return of([(this.constants ?? {}) as ConstantsLite, []] as [ConstantsLite, ConstantLite[]])
          }),
        )
        .subscribe(([constants, croptypes]: [ConstantsLite, ConstantLite[]]) => {
          this.constants = constants
          this.croptypes = croptypes
          this.logger.log('Constants initialized')
          resolve()
        })
    })
  }

  private getAll(language: string): Observable<[Constants, Croptype[]]> {
    let constantsParams: HttpParams = new HttpParams()
    constantsParams = constantsParams.append('languageName', language)
    const constantsOptions: {
      params: HttpParams
    } = {
      params: constantsParams,
    }

    let croptypesParams: HttpParams = new HttpParams()
    croptypesParams = croptypesParams.append('filter', `languageName="${language}"`)
    const croptypesOptions: {
      params: HttpParams
    } = {
      params: croptypesParams,
    }
    const constants: Observable<Constants> = this.http.get<Constants>(this.baseUrl, constantsOptions)
    const croptypes: Observable<Croptype[]> = this.http
      .get<MsSqlPaginatedData<Croptype>>(`${environment.apiUrl}${environment.croptypes}`, croptypesOptions)
      .pipe(map((v) => v.data))

    return forkJoin([constants, croptypes])
  }

  get(constant: string, languageName?: string): Observable<Constant> {
    let params: HttpParams = new HttpParams()
    if (languageName) {
      params = params.append('languageName', languageName)
    }
    const options: {
      params: HttpParams
    } = {
      params,
    }

    return this.http.get<Constant>(`${this.baseUrl}/${constant}`, options)
  }
}
